/**
 * Delimited tables — the BOM, and anything else in columns. Pure, no DOM.
 *
 * RFC 4180 in the parts that matter: a field may be quoted, a quote inside one
 * is doubled, and a quoted field may span lines. Written back, a field is
 * quoted only when it has to be, so a file that needed no quotes keeps none
 * and a round trip through the editor does not rewrite the whole thing.
 */

/** What separates the fields. Comma unless the file plainly says otherwise. */
export type Delimiter = ',' | '\t' | ';';

export interface Table {
  rows: string[][];
  delimiter: Delimiter;
  /** The line ending the file arrived with, restored on the way out. */
  eol: '\n' | '\r\n';
  /** Whether the last row ended with a line break. */
  trailingBreak: boolean;
}

const CANDIDATES: Delimiter[] = [',', '\t', ';'];

/**
 * Which character separates the fields.
 *
 * Counted outside quotes over the first few lines, and the winner has to
 * appear on every one of them — a comma inside one description is not a
 * delimiter, and picking it would split that row and no other.
 */
export function detectDelimiter(text: string): Delimiter {
  const lines = splitRowsRaw(text.slice(0, 8000)).slice(0, 6).filter((l) => l !== '');
  if (lines.length === 0) return ',';

  let best: Delimiter = ',';
  let bestCount = 0;
  for (const d of CANDIDATES) {
    const counts = lines.map((l) => countOutsideQuotes(l, d));
    const first = counts[0] ?? 0;
    if (first === 0) continue;
    if (!counts.every((c) => c === first)) continue;
    if (first > bestCount) { best = d; bestCount = first; }
  }
  return best;
}

/** Rows on unquoted line breaks only, for sniffing. */
function splitRowsRaw(text: string): string[] {
  const out: string[] = [];
  let quoted = false;
  let start = 0;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '"') quoted = !quoted;
    else if (c === '\n' && !quoted) {
      out.push(text.slice(start, i).replace(/\r$/, ''));
      start = i + 1;
    }
  }
  out.push(text.slice(start));
  return out;
}

function countOutsideQuotes(line: string, d: string): number {
  let n = 0;
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') quoted = !quoted;
    else if (c === d && !quoted) n++;
  }
  return n;
}

/** Read a delimited file into rows of fields. */
export function parseTable(text: string, delimiter?: Delimiter): Table {
  const d = delimiter ?? detectDelimiter(text);
  const eol: '\n' | '\r\n' = /\r\n/.test(text) ? '\r\n' : '\n';
  const trailingBreak = /\r?\n$/.test(text);

  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  let i = 0;

  const endField = (): void => { row.push(field); field = ''; };
  const endRow = (): void => { endField(); rows.push(row); row = []; };

  while (i < text.length) {
    const c = text[i]!;
    if (quoted) {
      if (c === '"') {
        // A doubled quote is one quote; a single one closes the field.
        if (text[i + 1] === '"') { field += '"'; i += 2; continue; }
        quoted = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"' && field === '') { quoted = true; i++; continue; }
    if (c === d) { endField(); i++; continue; }
    if (c === '\r' && text[i + 1] === '\n') { endRow(); i += 2; continue; }
    if (c === '\n' || c === '\r') { endRow(); i++; continue; }
    field += c;
    i++;
  }
  // A file ending in a line break has no further row; one ending mid-row does.
  if (field !== '' || row.length > 0) endRow();

  return { rows, delimiter: d, eol, trailingBreak };
}

/** Write rows back out, quoting only what needs it. */
export function writeTable(table: Table): string {
  const { rows, delimiter: d, eol, trailingBreak } = table;
  const cell = (v: string): string => (needsQuotes(v, d) ? `"${v.replace(/"/g, '""')}"` : v);
  const body = rows.map((r) => r.map(cell).join(d)).join(eol);
  return trailingBreak && body !== '' ? body + eol : body;
}

function needsQuotes(v: string, d: string): boolean {
  if (v === '') return false;
  if (v.includes(d) || v.includes('"') || v.includes('\n') || v.includes('\r')) return true;
  // Leading or trailing space survives only inside quotes.
  return v !== v.trim();
}

/** The widest row, so a grid knows how many columns to draw. */
export function columnCount(rows: string[][]): number {
  let n = 0;
  for (const r of rows) if (r.length > n) n = r.length;
  return n;
}

/** Spreadsheet column names: A, B … Z, AA, AB … */
export function columnName(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}
